import { DropDownTreeChangeEvent, ItemProps } from '@progress/kendo-react-dropdowns';
import { GridCellProps } from '@progress/kendo-react-grid';
import { isEmpty } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import GridCellBase, { useGridCellDisplayValue } from 'src/components/GridCellBase';
import TreeDropDown from 'src/components/design-system/DropDownTree';
import useViewBudgetPeriod from 'src/hooks/useViewBudgetPeriod';
import { ClosedTag } from 'src/sections/view_checkbook/PeriodDropdown';
import { GlPeriodObject } from 'src/services/useGLPeriodApi';
import { getBgColor } from 'src/utils/icons';
import { GLPeriodDropdownChildren } from 'src/utils/transformation';
import { processTreeData } from 'src/utils/treeDataOperations';

const selectField = 'selected';
const expandField = 'expanded';
const dataItemKey = 'id';
const textField = 'content';
const subItemsField = 'items';
const fields = { selectField, expandField, dataItemKey, subItemsField };

const BudgetPeriodCell = (props: GridCellProps) => {
  const { field, dataItem, dataIndex, onChange } = props;
  const { dropDownValues, glPeriods, budgetPeriods, isReadOnly, currentBudgetPeriod } =
    useViewBudgetPeriod();
  const { value } = useGridCellDisplayValue(props);
  const [expanded, setExpanded] = useState([] as number[]);
  const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);

  const findBudgetPeriod = glPeriods.find(
    (item) => item.glPeriodId === Number(value)
  ) as GlPeriodObject;

  const dropDownValue = useMemo(() => {
    const currentGlPeriods = dropDownValues.find(
      (value) => value.fiscalYear === findBudgetPeriod?.fiscalYear
    )?.items as GLPeriodDropdownChildren[];
    return currentGlPeriods
      ? currentGlPeriods.find((period) => period.glPeriodId === findBudgetPeriod?.glPeriodId)
      : null;
  }, [dropDownValues, findBudgetPeriod?.fiscalYear, findBudgetPeriod?.glPeriodId]);

  const initialExpandedPeriod = useCallback(() => {
    const initialExpand = dropDownValue
      ? [dropDownValue?.fiscalYear]
      : !isEmpty(dropDownValues)
      ? [currentBudgetPeriod.fiscalYear]
      : [];
    setExpanded(initialExpand);
  }, [currentBudgetPeriod.fiscalYear, dropDownValue, dropDownValues]);

  const otherGlPeriodsUsed = useMemo(() => {
    let glPeriodIds: number[] = [];
    budgetPeriods.forEach((item) => {
      glPeriodIds.push(item.glPeriodId);
    });
    return glPeriodIds;
  }, [budgetPeriods]);

  const handleChange = useCallback(
    (event: DropDownTreeChangeEvent) => {
      const { level = [], value = {} } = event;
      const { fiscalYear = 0, glPeriodId, isClosed } = value || {};

      if (otherGlPeriodsUsed.includes(glPeriodId) || Number(isClosed) === 1) {
        return;
      }
      if (isEmpty(level) || level.length > 1) {
        setDropDownOpened(false);
        onChange?.({
          dataIndex,
          dataItem,
          syntheticEvent: event.syntheticEvent,
          field,
          value: glPeriodId ?? null,
        });
      } else if (level.length === 1) {
        setExpanded((prev) => {
          if (prev.includes(fiscalYear)) {
            return prev.filter((item) => item !== fiscalYear);
          } else {
            return [...prev, fiscalYear];
          }
        });
      }
    },
    [dataIndex, dataItem, field, onChange, otherGlPeriodsUsed]
  );

  const Item = useCallback(
    (props: ItemProps) => {
      const { selected = false, content, parent = false, glPeriodId, isClosed = 0 } = props.item;
      const alreadyUsed = otherGlPeriodsUsed.includes(glPeriodId);
      const isClosedPeriod = Number(isClosed) === 1;

      return (
        <span className="text-xs">
          {parent ? (
            <span onClick={() => setDropDownOpened(true)}>{content}</span>
          ) : alreadyUsed ? (
            <span
              style={{
                color: `${selected ? '' : '#d0d0d0'}`,
                cursor: 'not-allowed',
              }}
            >
              {content}
              {isClosedPeriod && <ClosedTag selected />}
            </span>
          ) : (
            <span
              className={isClosedPeriod ? 'selected-period' : `cursor-default hover:cursor-pointer`}
            >
              {content} {isClosedPeriod && <ClosedTag />}
            </span>
          )}
        </span>
      );
    },
    [otherGlPeriodsUsed]
  );

  const treeData = useMemo(
    () => processTreeData(dropDownValues, { expanded, dropDownValue }, fields),
    [dropDownValue, dropDownValues, expanded]
  );

  const error = useMemo(() => dataItem?.errors?.period, [dataItem?.errors?.period]);

  return (
    <GridCellBase
      {...props}
      title={
        !isEmpty(dropDownValue)
          ? `FY${dropDownValue?.fiscalYear}-${dropDownValue?.content}`
          : 'Select accounting period'
      }
      className={!isReadOnly ? getBgColor(error) : ''}
    >
      {isReadOnly ? (
        <p>
          {dropDownValue?.fiscalYear && dropDownValue?.content
            ? `FY${dropDownValue?.fiscalYear ?? ''}-${dropDownValue?.content ?? ''}`
            : ''}
        </p>
      ) : (
        <TreeDropDown
          key={dataItem.rowId}
          value={dropDownValue}
          placeholder="Select accounting period"
          data={treeData}
          onChange={handleChange}
          expanded={expanded}
          setExpanded={setExpanded}
          dropDownOpened={dropDownOpened}
          setDropDownOpened={setDropDownOpened}
          textField={textField}
          dataItemKey={dataItemKey}
          selectField={selectField}
          expandField={expandField}
          subItemsField={subItemsField}
          item={Item}
          valid={!error?.length}
          popupSettings={{
            className: '!w-[300px]',
          }}
          setInitialExpandValue={initialExpandedPeriod}
        />
      )}
    </GridCellBase>
  );
};

export default BudgetPeriodCell;
